import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { toApiDateTime } from '../../utils/dateTimeLocal';
import ScheduleDateTimePicker, { addMinutesToLocalValue } from '../ui/ScheduleDateTimePicker';
import {
  D28D_SCHEDULE_TZ,
  formatDateTimeInScheduleTz,
  hourInScheduleTz,
  sameScheduleDay,
  scheduleTzLocalToUtcIso,
} from '../../utils/d28dScheduleTimezone';
import SearchableSelect from '../ui/SearchableSelect';
import './D28dSchedulingCalendar.css';

const VIEWS = { MONTH: 'month', WEEK: 'week', DAY: 'day' };

/** Solo filas con al menos una clase programada (sin franjas vacías 05:00–21:00). */
function buildHourRange(items) {
  const hours = new Set();
  items.forEach((c) => {
    const h = hourInScheduleTz(c.start_time);
    if (Number.isFinite(h)) hours.add(h);
  });
  if (!hours.size) return [];
  return [...hours].sort((a, b) => a - b);
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function formatEventTime(iso) {
  try {
    return new Intl.DateTimeFormat('es-CO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: D28D_SCHEDULE_TZ,
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function formatEventTimeRange(startIso, endIso) {
  const start = formatEventTime(startIso);
  const end = formatEventTime(endIso);
  if (!start) return '';
  if (!end || start === end) return start;
  return `${start} – ${end}`;
}

function sortClassesChronologically(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.start_time).getTime();
    const tb = new Date(b.start_time).getTime();
    if (ta !== tb) return ta - tb;
    return new Date(a.end_time).getTime() - new Date(b.end_time).getTime();
  });
}

const DEFAULT_FORM = {
  title: 'Sesión D28D',
  program_id: 'virtual_d28d',
  zoom_account_id: 'virtual_d28d_1',
  auto_zoom: true,
  use_routine: true,
  d28d_routine_id: '',
  start_time: '',
  end_time: '',
};

export default function D28dSchedulingCalendar({
  programs = [],
  items = [],
  loadingItems = false,
  onRefetch,
  onSaved,
  onEditClass,
  classTypeFilter = '',
  coachFilter = '',
  scopeGymId = null,
  isGymScoped = false,
}) {
  const [view, setView] = useState(VIEWS.WEEK);
  const [anchor, setAnchor] = useState(() => new Date());
  const [routines, setRoutines] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cloningWeek, setCloningWeek] = useState(false);

  const refreshItems = useCallback(async () => {
    if (onRefetch) await onRefetch();
    onSaved?.();
  }, [onRefetch, onSaved]);

  const fetchRoutines = useCallback(async () => {
    try {
      const params = { estado: 'activa' };
      if (isGymScoped && scopeGymId) {
        params.gym_id = scopeGymId;
        params.gym_only = 'true';
      }
      const resp = await api.get('/d28d/routines', { params });
      setRoutines(resp.data?.data || []);
    } catch {
      setRoutines([]);
    }
  }, [isGymScoped, scopeGymId]);

  useEffect(() => {
    fetchRoutines();
  }, [fetchRoutines]);

  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const programOptions = useMemo(
    () => (programs.length
      ? programs.map((p) => ({ value: p.id, label: p.name || p.nombre || p.id }))
      : [
        { value: 'virtual_d28d', label: 'Virtual D28D' },
        { value: 'vital', label: 'Vital' },
        { value: 'pancitas', label: 'Pancitas' },
      ]),
    [programs],
  );

  const routineOptions = useMemo(
    () => routines.map((r) => ({
      value: r.id,
      label: r.nombre,
      hint: `${r.categoria}${r.subcategoria ? ` · ${r.subcategoria}` : ''}`,
    })),
    [routines],
  );

  const classesInRange = useMemo(() => {
    return items.filter((c) => {
      const s = new Date(c.start_time);
      let inRange = true;
      if (view === VIEWS.DAY) inRange = sameDay(s, anchor);
      else if (view === VIEWS.WEEK) {
        const end = addDays(weekStart, 7);
        inRange = s >= weekStart && s < end;
      } else {
        inRange = s.getMonth() === anchor.getMonth() && s.getFullYear() === anchor.getFullYear();
      }
      if (!inRange) return false;
      if (classTypeFilter && !String(c.class_type || 'METODO D28D').toUpperCase().includes(classTypeFilter.split(' ')[0])) {
        return false;
      }
      if (coachFilter) {
        if (coachFilter.startsWith('name:')) {
          if (String(c.coach || '').trim() !== coachFilter.slice(5)) return false;
        } else if (String(c.d28d_host_user_id || '') !== String(coachFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [items, view, anchor, weekStart, classTypeFilter, coachFilter]);

  const hours = useMemo(
    () => buildHourRange(classesInRange),
    [classesInRange],
  );

  const openSlot = (day, hour) => {
    const y = day.getFullYear();
    const mo = String(day.getMonth() + 1).padStart(2, '0');
    const d = String(day.getDate()).padStart(2, '0');
    const localStart = `${y}-${mo}-${d}T${pad(hour)}:00`;
    const startIso = scheduleTzLocalToUtcIso(localStart);
    const endIso = scheduleTzLocalToUtcIso(`${y}-${mo}-${d}T${pad(hour + 1)}:00`);
    const start = startIso ? new Date(startIso) : new Date(day);
    const end = endIso ? new Date(endIso) : new Date(start.getTime() + 3600000);
    setModal({ start, end });
    setForm({
      ...DEFAULT_FORM,
      program_id: programOptions[0]?.value || 'virtual_d28d',
      start_time: formatDateTimeInScheduleTz(start.toISOString()),
      end_time: formatDateTimeInScheduleTz(end.toISOString()),
    });
    setError('');
  };

  const onRoutinePick = (routineId) => {
    const r = routines.find((x) => String(x.id) === String(routineId));
    setForm((prev) => ({
      ...prev,
      d28d_routine_id: routineId,
      title: prev.title.trim() && prev.title !== 'Sesión D28D' ? prev.title : (r?.nombre || prev.title),
    }));
  };

  const saveClass = async (e) => {
    e.preventDefault();
    if (!form.program_id || !form.start_time || !form.end_time) {
      setError('Programa, inicio y fin son requeridos.');
      return;
    }
    if (form.use_routine && !form.d28d_routine_id) {
      setError('Selecciona una rutina o desactiva «Usar rutina D28D».');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await api.post('/live-classes/admin', {
        title: form.title || 'Sesión D28D',
        program_id: form.program_id,
        zoom_account_id: form.program_id === 'virtual_d28d' ? form.zoom_account_id : null,
        start_time: toApiDateTime(form.start_time),
        end_time: toApiDateTime(form.end_time),
        auto_zoom: form.auto_zoom,
        d28d_routine_id: form.use_routine && form.d28d_routine_id ? Number(form.d28d_routine_id) : null,
        is_global: true,
        active: true,
        source_module: 'd28d',
      });
      setModal(null);
      await refreshItems();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la clase.');
    } finally {
      setSaving(false);
    }
  };

  const shift = (delta) => {
    const x = new Date(anchor);
    if (view === VIEWS.MONTH) x.setMonth(x.getMonth() + delta);
    else if (view === VIEWS.WEEK) x.setDate(x.getDate() + delta * 7);
    else x.setDate(x.getDate() + delta);
    setAnchor(x);
  };

  const handleCloneWeek = async () => {
    if (cloningWeek) return;
    const monday = startOfWeek(anchor);
    const yyyy = monday.getFullYear();
    const mm = String(monday.getMonth() + 1).padStart(2, '0');
    const dd = String(monday.getDate()).padStart(2, '0');
    if (!window.confirm('¿Clonar toda la programación de esta semana a la siguiente (+7 días, mismas clases)?')) {
      return;
    }
    setCloningWeek(true);
    setError('');
    try {
      const res = await api.post('/live-classes/admin/clone-week', {
        source_week_start: `${yyyy}-${mm}-${dd}`,
        ...(scopeGymId != null && scopeGymId !== '' ? { gym_id: Number(scopeGymId) } : {}),
      });
      const data = res.data?.data || {};
      await refreshItems();
      setView(VIEWS.WEEK);
      setAnchor(addDays(monday, 7));
      setError('');
      // eslint-disable-next-line no-alert
      window.alert(`Semana clonada: ${data.created_count || 0} creadas, ${data.skipped_count || 0} omitidas.`);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo clonar la semana.');
    } finally {
      setCloningWeek(false);
    }
  };

  const openEdit = (c, ev) => {
    ev?.stopPropagation();
    if (onEditClass) {
      onEditClass(c);
      return;
    }
    setError('La edición desde el calendario no está disponible. Usa la pestaña Lista.');
  };

  const renderEvent = (c) => {
    const range = formatEventTimeRange(c.start_time, c.end_time);
    return (
      <button
        key={c.id}
        type="button"
        className="d28d-sched-cal__event"
        title={`${c.title} · ${range} — Clic para editar`}
        onClick={(ev) => openEdit(c, ev)}
      >
        <span className="d28d-sched-cal__event-title">{c.title}</span>
        <span className="d28d-sched-cal__event-range">{range}</span>
      </button>
    );
  };

  return (
    <div className="d28d-sched-cal surface-readable rounded-xl p-4 border border-slate-200">
      {loadingItems ? (
        <p className="text-sm text-stone-500 mb-3">Actualizando clases…</p>
      ) : null}
      <div className="d28d-sched-cal__toolbar">
        <div className="flex flex-wrap gap-2">
          {Object.entries(VIEWS).map(([k, v]) => (
            <button
              key={v}
              type="button"
              className={`d28d-sched-cal__view-btn ${view === v ? 'd28d-sched-cal__view-btn--active' : ''}`}
              onClick={() => setView(v)}
            >
              {k === 'MONTH' ? 'Mes' : k === 'WEEK' ? 'Semana' : 'Día'}
            </button>
          ))}
        </div>
        <div className="d28d-sched-cal__nav">
          <button type="button" className="btn-secondary text-sm py-1 px-2" onClick={() => shift(-1)} aria-label="Anterior">←</button>
          <span className="d28d-sched-cal__nav-label">
            {anchor.toLocaleDateString('es-CO', { month: 'long', year: 'numeric', day: view === VIEWS.DAY ? 'numeric' : undefined })}
          </span>
          <button type="button" className="btn-secondary text-sm py-1 px-2" onClick={() => shift(1)} aria-label="Siguiente">→</button>
          <button type="button" className="btn-secondary text-sm" onClick={() => setAnchor(new Date())}>Hoy</button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={cloningWeek}
            onClick={handleCloneWeek}
            title="Copia todas las clases de la semana visible a la semana siguiente"
          >
            {cloningWeek ? 'Clonando…' : 'Clonar semana → siguiente'}
          </button>
        </div>
      </div>

      <p className="text-xs d28d-text-muted mt-3 mb-2">
        Clic en una clase para editarla. Clic en celda vacía para programar una nueva.
      </p>

      {view === VIEWS.WEEK && (
        <div className="d28d-sched-cal__grid-wrap mt-2">
          {hours.length === 0 ? (
            <p className="text-sm text-stone-500 py-6 text-center">
              No hay clases en esta semana. Programa desde la pestaña Lista, importa Excel o cambia de semana.
            </p>
          ) : (
          <table className="d28d-sched-cal__grid">
            <thead>
              <tr>
                <th aria-hidden />
                {weekDays.map((d) => (
                  <th key={d.toISOString()}>
                    {d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map((hour) => (
                <tr key={hour}>
                  <td className="d28d-sched-cal__hour" title="Hora de inicio de la franja">
                    {pad(hour)}:00
                  </td>
                  {weekDays.map((day) => {
                    const cellClasses = sortClassesChronologically(classesInRange.filter((c) => (
                      sameScheduleDay(c.start_time, day) && hourInScheduleTz(c.start_time) === hour
                    )));
                    const isToday = sameDay(day, today);
                    return (
                      <td
                        key={`${day.toISOString()}-${hour}`}
                        className={`d28d-sched-cal__cell${isToday ? ' d28d-sched-cal__cell--today' : ''}`}
                        onClick={() => openSlot(day, hour)}
                        title="Clic para nueva clase"
                      >
                        <div className="d28d-sched-cal__cell-events">
                          {cellClasses.map(renderEvent)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      )}

      {view === VIEWS.DAY && (
        <div className="space-y-2 mt-2">
          {hours.map((hour) => {
            const slotClasses = sortClassesChronologically(classesInRange.filter((c) => (
              sameScheduleDay(c.start_time, anchor) && hourInScheduleTz(c.start_time) === hour
            )));
            if (!slotClasses.length) {
              return (
                <button
                  key={hour}
                  type="button"
                  className="d28d-sched-cal__day-slot"
                  onClick={() => openSlot(anchor, hour)}
                >
                  <span className="d28d-sched-cal__day-slot-time">{pad(hour)}:00</span>
                  <span className="d28d-sched-cal__day-slot-label">+ Nueva clase</span>
                </button>
              );
            }
            return (
              <div key={hour} className="d28d-sched-cal__day-slot d28d-sched-cal__day-slot--filled">
                <span className="d28d-sched-cal__day-slot-time">{pad(hour)}:00</span>
                <div className="d28d-sched-cal__day-slot-events">
                  {slotClasses.map(renderEvent)}
                  <button
                    type="button"
                    className="d28d-sched-cal__day-slot-add"
                    onClick={() => openSlot(anchor, hour)}
                  >
                    + Nueva clase
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === VIEWS.MONTH && (
        <div className="grid grid-cols-7 gap-1.5 mt-2">
          {Array.from({ length: 35 }, (_, i) => {
            const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
            const start = startOfWeek(first);
            const day = addDays(start, i);
            const count = classesInRange.filter((c) => sameDay(new Date(c.start_time), day)).length;
            const inMonth = day.getMonth() === anchor.getMonth();
            return (
              <button
                key={i}
                type="button"
                className={`d28d-sched-cal__month-cell${inMonth ? '' : ' d28d-sched-cal__month-cell--muted'}`}
                onClick={() => {
                  setAnchor(day);
                  setView(VIEWS.DAY);
                }}
              >
                <div className="font-bold text-sm">{day.getDate()}</div>
                {count > 0 ? <div className="d28d-sched-cal__month-count">{count} clase(s)</div> : null}
              </button>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="d28d-sched-cal__modal-backdrop" role="dialog" aria-modal="true">
          <form className="d28d-sched-cal__modal form-modal d28d-admin-modal surface-readable ui-light-panel" onSubmit={saveClass}>
            <div className="d28d-sched-cal__modal-header">
              <h3 className="mb-1" style={{ color: '#0f172a' }}>Nueva clase</h3>
              <p className="ui-field__hint mb-0">Completa en dos columnas. Rutina y programa con autocompletado.</p>
            </div>
            <div className="d28d-sched-cal__modal-body">
            {error ? (
              <p className="text-sm text-red-800 bg-red-50 border border-red-300 rounded-lg px-3 py-2 mb-3">{error}</p>
            ) : null}
            <label className="ui-check-row mb-3">
              <input
                type="checkbox"
                checked={!!form.use_routine}
                onChange={(e) => setForm({ ...form, use_routine: e.target.checked })}
              />
              <span>Usar rutina D28D existente</span>
            </label>

            <div className="d28d-sched-cal__modal-grid">
              {form.use_routine && (
                <div className="span-2">
                  <SearchableSelect
                    label="Rutina"
                    value={form.d28d_routine_id}
                    onChange={onRoutinePick}
                    options={routineOptions}
                    placeholder="Buscar rutina por nombre o categoría…"
                    required={form.use_routine}
                  />
                </div>
              )}

              <label className="block ui-field span-2 sm:span-1">
                <span className="ui-field__label">Título de sesión</span>
                <input
                  className="ui-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </label>

              <SearchableSelect
                label="Programa"
                value={form.program_id}
                onChange={(v) => setForm({ ...form, program_id: v, zoom_account_id: v === 'virtual_d28d' ? form.zoom_account_id : form.zoom_account_id })}
                options={programOptions}
                placeholder="Buscar programa…"
                required
              />

              {form.program_id === 'virtual_d28d' && (
                <label className="block ui-field">
                  <span className="ui-field__label">Cuenta Zoom</span>
                  <select
                    className="ui-input"
                    value={form.zoom_account_id}
                    onChange={(e) => setForm({ ...form, zoom_account_id: e.target.value })}
                  >
                    <option value="virtual_d28d_1">Cuenta 1 — D28dzoom1@gmail.com</option>
                    <option value="virtual_d28d_2">Cuenta 2 — d28dzoom2@gmail.com</option>
                  </select>
                </label>
              )}

              <div className="span-2">
                <ScheduleDateTimePicker
                  label="Inicio"
                  value={form.start_time}
                  onChange={(v) => setForm({ ...form, start_time: v })}
                  required
                  id="cal-start"
                />
              </div>

              <div className="span-2">
                <ScheduleDateTimePicker
                  label="Fin"
                  value={form.end_time}
                  onChange={(v) => setForm({ ...form, end_time: v })}
                  required
                  id="cal-end"
                  durationPresets={[45, 60, 90]}
                  onDurationPreset={(m) => setForm({ ...form, end_time: addMinutesToLocalValue(form.start_time, m) })}
                />
              </div>

              <label className="ui-check-row span-2">
                <input
                  type="checkbox"
                  checked={!!form.auto_zoom}
                  onChange={(e) => setForm({ ...form, auto_zoom: e.target.checked })}
                />
                <span>Generar enlace Zoom automático</span>
              </label>
            </div>
            </div>

            <div className="d28d-sched-cal__modal-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar clase'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
